/**
 * network-aware-fetch — 裸 fetch 的 typed 包装：把「没拿到 HTTP 响应」翻译成 AdminNetworkError。
 *
 * Why 独立成模块（不塞进 session-refresh）：本包装与会话续期无关，是所有对外请求的共同底座。
 * admin 目前有两类出网路径——`authedFetch`（绝大多数）与 `upload-image` 的 multipart 直传
 * （不能走 apiRequest，否则 JSON Content-Type 会破坏 multipart boundary）。两者都必须做同一件事：
 * DNS 失败 / 连接被阻断 / 断网 / 扩展拦截 / CORS 拒绝时，让调用方拿到「无法连接服务器」而不是
 * 浏览器方言的裸 TypeError（"Failed to fetch" / "NetworkError when attempting to fetch resource"）。
 */

import { AdminNetworkError, AdminRequestTimeoutError } from './errors';

/**
 * 默认硬超时。没有它时，半开连接会让 fetch 永不 settle（#356）。
 * 比 refresh 的 15s 长，又足以覆盖 admin 最慢的同步写；更久的请求自己传 signal / timeoutMs。
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

/**
 * 调用方 signal 与硬超时取并集。调用方 signal 只表示生命周期取消（卸载），
 * 不能顶掉 30s 硬超时——组件一直挂着、连接半开时仍必须 settle。
 */
export function composeHardTimeout(
  caller: AbortSignal | null | undefined,
  timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
): { signal: AbortSignal; didTimeout: () => boolean; abort: () => void } {
  const controller = new AbortController();
  let timedOut = false;
  setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  if (caller) {
    if (caller.aborted) controller.abort();
    else caller.addEventListener('abort', () => controller.abort(), { once: true });
  }
  // `abort` 暴露给在飞写请求登记（#356）：通知条的「停止等待」要能真正掐掉这一条请求。
  // 不复用 `caller`——它可能为空（直连调用方多数不传 signal），而那批正是最需要出口的。
  return { signal: controller.signal, didTimeout: () => timedOut, abort: () => controller.abort() };
}

/**
 * 发请求；仅当**请求本身没拿到 HTTP 响应**时抛 `AdminNetworkError`。
 *
 * 调用方主动取消的 AbortError 原样上抛（refresh 超时按瞬时故障、保留会话）。
 * 本函数自己装的硬超时则映射成 `AdminRequestTimeoutError`，让 multipart 直传
 * 也拿到 `serverStateUnknown`，而不是被 `res.json().catch` 吞成 INTERNAL_ERROR。
 */
export async function fetchOrThrowNetworkError(
  input: string,
  init: RequestInit = {},
  timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
): Promise<Response> {
  let requestInit = init;
  let didTimeout: (() => boolean) | undefined;
  if (!init.signal) {
    const composed = composeHardTimeout(undefined, timeoutMs);
    didTimeout = composed.didTimeout;
    requestInit = { ...init, signal: composed.signal };
  }
  try {
    return await fetch(input, requestInit);
  } catch (err) {
    if ((err as { name?: string } | null)?.name === 'AbortError') {
      if (didTimeout?.()) {
        throw new AdminRequestTimeoutError(init.method ?? 'GET', timeoutMs);
      }
      throw err;
    }
    throw new AdminNetworkError(err);
  }
}
